import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button, type SelectOption } from '@skupreme/shared/ui';
import { FormSelect } from '@skupreme/shared/ui/form';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import { LoaderBar } from '@components/LoaderBar';
import PurchaseOrdersService from '@utility/services/purchase-order';
import { SkupremeModal } from '@elements/Modal';
import { CurrencyUtility } from '@utility/currency';
import ItemService from '@utility/services/item';

const itemCurrencySchema = z.object({
  currency: z.string().trim().min(1, 'Currency is required'),
});

type ItemCurrencyForm = z.infer<typeof itemCurrencySchema>;

const DEFAULT_CURRENCY = 'USD';

export function EditItemCurrencyPopup({ open, closeModal, poId, item }) {
  const [saving, setSaving] = useState('');
  const { handleSubmit, control, reset } = useForm<ItemCurrencyForm>({
    resolver: zodResolver(itemCurrencySchema),
    defaultValues: { currency: DEFAULT_CURRENCY },
  });

  const listCurrencies = useMemo<SelectOption[]>(() => {
    const codes = new Set<string>(
      Object.values(CurrencyUtility.countryCurrencyCode || {}),
    );
    return [...codes].map(currency => ({ value: currency, label: currency }));
  }, []);

  useEffect(() => {
    reset({
      currency: (open && item?._id && item.currency) || DEFAULT_CURRENCY,
    });
  }, [open, item, reset]);

  const onSubmit = async (formData: ItemCurrencyForm) => {
    try {
      setSaving('Saving');
      if (poId) {
        const bodyData = {
          items: [
            {
              item_upc: item.item_upc,
              prices: item.prices,
              supplier_id: item.supplier_id,
              currency: formData.currency,
            },
          ],
        };
        await PurchaseOrdersService.updateItemPrices(poId, bodyData);
      } else {
        const bodyData = {
          items: [
            {
              item_upc: item.item_upc,
              supplier_id: item.supplier_id,
              lead_time: item.lead_time || 0,
              prices: item.prices,
              currency: formData.currency,
            },
          ],
        };
        await ItemService.update(bodyData);
      }

      ToastMessage.success('Item Currency has been updated successfully.');
      closeModal(formData.currency);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving('');
    }
  };

  return (
    <SkupremeModal
      open={open}
      title="Edit Currency"
      onClose={() => closeModal()}
      size="mini"
    >
      {saving && <LoaderBar content={saving} />}
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="sk:flex sk:flex-col sk:gap-4"
      >
        <FormSelect
          control={control}
          name="currency"
          label="Currency"
          options={listCurrencies}
          isRequired
        />
        <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
          <Button onPress={() => closeModal()}>Cancel</Button>
          <Button type="submit" variant="primary">
            Save
          </Button>
        </div>
      </form>
    </SkupremeModal>
  );
}
